/**
 * =============================================================================
 * Componente: CampaignCountdownBadge
 * Versão: 1.0.0 (2026-05-01)
 *
 * Descrição:
 *   Badge de contador regressivo (escassez) sobreposto à capa do
 *   `CampaignCard`. Renderiza apenas quando há `endsAt` definido.
 *
 *   Variantes visuais conforme proximidade do prazo:
 *     • running  (>24h)   → pílula neutra (preto/branco translúcido)
 *     • urgent   (≤24h)   → âmbar
 *     • critical (≤1h)    → vermelho com pulso suave
 *     • ended             → cinza ("Encerrada")
 *     • not_started       → azul ("Inicia em …")
 *
 *   Acessibilidade:
 *     - `role="timer"` + `aria-live="polite"` + `aria-atomic="true"`
 *     - `title` mostra a data/hora absoluta no fuso do usuário
 *     - Respeita `prefers-reduced-motion` (sem pulso)
 *
 *   Side-effect opcional:
 *     - `onEnd?` é chamado uma única vez quando o status transita para
 *       `ended`. Usado pelo card para invalidar o cache da listagem.
 * =============================================================================
 */
import { useEffect, useRef } from 'react';
import { Clock, AlarmClock, Hourglass, CalendarClock } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  useCountdown,
  formatCountdownCompact,
  type CountdownStatus,
} from '@/hooks/useCountdown';

interface Props {
  /** ISO timestamptz (vindo da RPC). */
  endsAt: string | null | undefined;
  /** ISO timestamptz opcional para "inicia em ...". */
  startsAt?: string | null | undefined;
  /** Disparado uma vez quando o contador atinge `ended`. */
  onEnd?: () => void;
  className?: string;
}

/** Mapa visual por status — usa tokens de design do portal afiliado. */
function variantClasses(status: CountdownStatus): string {
  switch (status) {
    case 'critical':
      // Vermelho com pulso (motion-safe). Texto branco para contraste.
      return 'bg-red-600/95 text-white shadow-md motion-safe:animate-pulse';
    case 'urgent':
      // Âmbar — alerta moderado.
      return 'bg-amber-500/95 text-black shadow-md';
    case 'ended':
      return 'bg-neutral-700/90 text-neutral-100';
    case 'not_started':
      return 'bg-sky-600/95 text-white';
    case 'running':
    default:
      return 'bg-[hsl(var(--aff-bg)/0.92)] text-[hsl(var(--aff-foreground))] backdrop-blur shadow-sm';
  }
}

/** Ícone por status. */
function StatusIcon({ status }: { status: CountdownStatus }) {
  const cls = 'h-3.5 w-3.5 shrink-0';
  switch (status) {
    case 'critical':
      return <AlarmClock className={cls} />;
    case 'urgent':
      return <Hourglass className={cls} />;
    case 'ended':
      return <Clock className={cls} />;
    case 'not_started':
      return <CalendarClock className={cls} />;
    default:
      return <Clock className={cls} />;
  }
}

/** Formata a data alvo no padrão pt-BR (dd/MM/yyyy HH:mm) para o tooltip. */
function formatAbsolute(d: Date | null): string {
  if (!d) return '';
  try {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(d);
  } catch {
    return d.toISOString();
  }
}

export function CampaignCountdownBadge({
  endsAt,
  startsAt,
  onEnd,
  className,
}: Props) {
  const cd = useCountdown(endsAt, { startsAt });
  const endedFiredRef = useRef(false);

  // Dispara o callback uma única vez ao encerrar.
  useEffect(() => {
    if (cd.status === 'ended' && !endedFiredRef.current) {
      endedFiredRef.current = true;
      onEnd?.();
    }
  }, [cd.status, onEnd]);

  // Sem data → não renderiza nada (campanha sem prazo).
  if (cd.status === 'idle') return null;

  // Constrói o texto do badge.
  let label: string;
  if (cd.status === 'ended') {
    label = 'Encerrada';
  } else if (cd.status === 'not_started') {
    label = `Inicia em ${formatCountdownCompact(cd)}`;
  } else {
    label = `Termina em ${formatCountdownCompact(cd)}`;
  }

  return (
    <span
      role="timer"
      aria-live="polite"
      aria-atomic="true"
      title={formatAbsolute(cd.target)}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold tabular-nums',
        variantClasses(cd.status),
        className,
      )}
    >
      <StatusIcon status={cd.status} />
      {label}
    </span>
  );
}
